import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  AccountBalance,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { reportsService } from '../../services/reports.service';
import { BranchProfitLossReportDto } from '../../types/reports.types';
import { CostCenterDto } from '../../types/costCenters.types';
import { costCentersService } from '../../services/costCenters.service';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';

const translations = {
  en: {
    title: 'Branch Profit & Loss Report',
    costCenter: 'Cost Center',
    allBranches: 'All Branches',
    fromDate: 'From Date',
    toDate: 'To Date',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    loading: 'Loading...',
    noData: 'No data available',
    revenue: 'Revenue',
    expenses: 'Expenses',
    totalRevenue: 'Total Revenue',
    totalExpenses: 'Total Expenses',
    netProfit: 'Net Profit',
    netLoss: 'Net Loss',
    accountCode: 'Code',
    accountName: 'Account Name',
    amount: 'Amount',
    kwd: 'KWD',
    period: 'Period',
    from: 'From',
    to: 'To',
  },
  ar: {
    title: 'تقرير الربح والخسارة للفروع',
    costCenter: 'مركز التكلفة',
    allBranches: 'جميع الفروع',
    fromDate: 'من تاريخ',
    toDate: 'إلى تاريخ',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    loading: 'جاري التحميل...',
    noData: 'لا توجد بيانات',
    revenue: 'الإيرادات',
    expenses: 'المصروفات',
    totalRevenue: 'إجمالي الإيرادات',
    totalExpenses: 'إجمالي المصروفات',
    netProfit: 'صافي الربح',
    netLoss: 'صافي الخسارة',
    accountCode: 'الكود',
    accountName: 'اسم الحساب',
    amount: 'المبلغ',
    kwd: 'د.ك',
    period: 'الفترة',
    from: 'من',
    to: 'إلى',
  },
};

const BranchProfitLossReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const t = translations[language];
  const { minDate, maxDate } = useFiscalDateRange();

  const [reportData, setReportData] = useState<BranchProfitLossReportDto | null>(null);
  const [costCenters, setCostCenters] = useState<CostCenterDto[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);

  const [selectedCostCenter, setSelectedCostCenter] = useState<number | null>(null);
  const [fromDate, setFromDate] = useState<string>(
    new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0]
  );
  const [toDate, setToDate] = useState<string>(
    new Date().toISOString().split('T')[0]
  );

  useEffect(() => {
    loadCostCenters();
    loadCompanyInfo();
    loadReport();
  }, []);

  const loadCostCenters = async () => {
    try {
      const data = await costCentersService.getAll();
      setCostCenters(data.filter(cc => cc.isActive));
    } catch (error) {
      console.error('Failed to load cost centers:', error);
    }
  };

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadReport = async () => {
    try {
      setLoading(true);
      const data = await reportsService.getBranchProfitLoss(
        selectedCostCenter,
        fromDate,
        toDate
      );
      setReportData(data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load report');
      console.error('Failed to load report:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-GB', {
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(Math.abs(value));
  };

  const formatWithSign = (value: number): string => {
    if (value < 0) {
      return `(${formatCurrency(value)})`;
    }
    return formatCurrency(value);
  };

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(isRTL ? 'ar-KW' : 'en-GB');
  };

  const getSelectedBranchName = (): string => {
    if (!selectedCostCenter) {
      return t.allBranches;
    }
    const branch = costCenters.find(cc => cc.costCenterId === selectedCostCenter);
    return branch ? (isRTL ? branch.costCenterNameAr : branch.costCenterName) : '';
  };

  const exportToExcel = async () => {
    if (!reportData) return;

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      // Company Info
      worksheet.mergeCells('B1:D1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FFFF6B35' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:D2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:D3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      // Title
      worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = t.title;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF6B35' }
      };

      currentRow++;

      // Branch & Period Info
      worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
      const branchCell = worksheet.getCell(`A${currentRow}`);
      branchCell.value = `${t.costCenter}: ${getSelectedBranchName()}`;
      branchCell.font = { size: 11, bold: true, color: { argb: 'FF2196F3' } };
      branchCell.alignment = { horizontal: 'center' };

      currentRow++;

      worksheet.mergeCells(`A${currentRow}:D${currentRow}`);
      const periodCell = worksheet.getCell(`A${currentRow}`);
      periodCell.value = `${t.period}: ${formatDate(fromDate)} ${t.to} ${formatDate(toDate)}`;
      periodCell.font = { size: 10, color: { argb: 'FF424242' } };
      periodCell.alignment = { horizontal: 'center' };

      currentRow += 2;

      // Headers
      worksheet.getCell(currentRow, 1).value = t.accountCode;
      worksheet.getCell(currentRow, 2).value = t.accountName;
      worksheet.getCell(currentRow, 3).value = t.amount;

      for (let i = 1; i <= 3; i++) {
        const cell = worksheet.getCell(currentRow, i);
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF2196F3' } };
        cell.alignment = { horizontal: i === 3 ? 'right' : 'left' };
      }

      currentRow++;

      // Revenue Section
      worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
      const revCell = worksheet.getCell(`A${currentRow}`);
      revCell.value = t.revenue;
      revCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      revCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF66BB6A' } };
      currentRow++;

      reportData.revenueAccounts.forEach((account) => {
        worksheet.getCell(currentRow, 1).value = account.accountCode;
        worksheet.getCell(currentRow, 2).value = isRTL ? account.accountNameAr : account.accountName;
        worksheet.getCell(currentRow, 3).value = formatCurrency(account.totalAmount);
        worksheet.getCell(currentRow, 3).alignment = { horizontal: 'right' };
        currentRow++;
      });

      // Total Revenue Row
      worksheet.getCell(currentRow, 1).value = '';
      worksheet.getCell(currentRow, 2).value = t.totalRevenue;
      worksheet.getCell(currentRow, 2).font = { bold: true };
      worksheet.getCell(currentRow, 3).value = formatCurrency(reportData.grandTotalRevenue);
      worksheet.getCell(currentRow, 3).font = { bold: true };
      worksheet.getCell(currentRow, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFC8E6C9' } };
      worksheet.getCell(currentRow, 3).alignment = { horizontal: 'right' };

      currentRow += 2;

      // Expenses Section
      worksheet.mergeCells(`A${currentRow}:C${currentRow}`);
      const expCell = worksheet.getCell(`A${currentRow}`);
      expCell.value = t.expenses;
      expCell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      expCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEF5350' } };
      currentRow++;

      reportData.expenseAccounts.forEach((account) => {
        worksheet.getCell(currentRow, 1).value = account.accountCode;
        worksheet.getCell(currentRow, 2).value = isRTL ? account.accountNameAr : account.accountName;
        worksheet.getCell(currentRow, 3).value = formatCurrency(account.totalAmount);
        worksheet.getCell(currentRow, 3).alignment = { horizontal: 'right' };
        currentRow++;
      });

      // Total Expenses Row
      worksheet.getCell(currentRow, 1).value = '';
      worksheet.getCell(currentRow, 2).value = t.totalExpenses;
      worksheet.getCell(currentRow, 2).font = { bold: true };
      worksheet.getCell(currentRow, 3).value = formatCurrency(reportData.grandTotalExpenses);
      worksheet.getCell(currentRow, 3).font = { bold: true };
      worksheet.getCell(currentRow, 3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFCDD2' } };
      worksheet.getCell(currentRow, 3).alignment = { horizontal: 'right' };

      currentRow += 2;

      // Net Profit/Loss Row
      worksheet.getCell(currentRow, 1).value = '';
      const netProfitLabel = worksheet.getCell(currentRow, 2);
      netProfitLabel.value = reportData.grandNetProfit >= 0 ? t.netProfit : t.netLoss;
      netProfitLabel.font = { bold: true, size: 12 };
      
      const netProfitCell = worksheet.getCell(currentRow, 3);
      netProfitCell.value = formatWithSign(reportData.grandNetProfit);
      netProfitCell.font = { bold: true, size: 12, color: { argb: reportData.grandNetProfit >= 0 ? 'FF66BB6A' : 'FFEF5350' } };
      netProfitCell.alignment = { horizontal: 'right' };

      // Column widths
      worksheet.getColumn(1).width = 12;
      worksheet.getColumn(2).width = 40;
      worksheet.getColumn(3).width = 18;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${t.title}_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);

      toast.success(t.exportExcel + ' ✔');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  const exportToPdf = async () => {
    if (!reportData) return;

    try {
      // استيراد html2canvas
      const html2canvas = (await import('html2canvas')).default;

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      let yPosition = 20;

      // Logo
      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', pageWidth / 2 - 15, 10, 30, 20);
          yPosition = 35;
        } catch (err) {
          console.warn('Could not add logo to PDF:', err);
        }
      }

      // Company Info
      const tempDiv = document.createElement('div');
      tempDiv.style.cssText = 'position: absolute; left: -9999px; width: 180mm; padding: 20px; text-align: center; font-family: Arial, sans-serif;';
      tempDiv.innerHTML = `
        <div style="font-size: 20px; font-weight: bold; color: #000; margin-bottom: 10px;">
          ${isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
        </div>
        <div style="font-size: 12px; color: #000; margin-bottom: 5px;">
          ${companyInfo?.address || ''}
        </div>
        <div style="font-size: 12px; color: #000; margin-bottom: 15px;">
          ${companyInfo?.phone || ''} | ${companyInfo?.email || ''}
        </div>
        <div style="background: #FF6B35; color: white; padding: 12px; font-size: 16px; font-weight: bold; margin-bottom: 15px;">
          ${t.title}
        </div>
        <div style="background: #E3F2FD; padding: 10px; margin-bottom: 15px;">
          <div style="color: #000; font-weight: bold; font-size: 14px; margin-bottom: 5px;">
            ${t.costCenter}: ${getSelectedBranchName()}
          </div>
          <div style="color: #000; font-size: 12px;">
            ${t.period}: ${formatDate(fromDate)} ${t.to} ${formatDate(toDate)}
          </div>
        </div>
      `;
      document.body.appendChild(tempDiv);

      const headerCanvas = await html2canvas(tempDiv, { 
        scale: 2,
        useCORS: true,
        logging: false 
      });
      document.body.removeChild(tempDiv);

      const headerImgData = headerCanvas.toDataURL('image/png');
      const headerHeight = (headerCanvas.height * pageWidth) / headerCanvas.width;
      doc.addImage(headerImgData, 'PNG', 10, yPosition, pageWidth - 20, headerHeight);
      yPosition += headerHeight + 5;

      // إنشاء جدول HTML كامل مع كل التفاصيل
      const tableDiv = document.createElement('div');
      tableDiv.style.cssText = 'position: absolute; left: -9999px; width: 180mm; padding: 10px; background: white;';
      
      let tableHTML = `
        <table style="width: 100%; border-collapse: collapse; font-family: Arial, sans-serif;">
          <thead>
            <tr style="background: #2196F3; color: white;">
              <th style="border: 1px solid #ddd; padding: 8px; text-align: ${isRTL ? 'right' : 'left'}; width: 15%;">${t.accountCode}</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: ${isRTL ? 'right' : 'left'}; width: 65%;">${t.accountName}</th>
              <th style="border: 1px solid #ddd; padding: 8px; text-align: right; width: 20%;">${t.amount}</th>
            </tr>
          </thead>
          <tbody>
            <tr style="background: #66BB6A; color: white;">
              <td colspan="3" style="border: 1px solid #ddd; padding: 8px; font-weight: bold;">${t.revenue}</td>
            </tr>
      `;

      // Revenue Accounts
      reportData.revenueAccounts.forEach((account) => {
        tableHTML += `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000;">${account.accountCode}</td>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000; text-align: ${isRTL ? 'right' : 'left'};">${isRTL ? account.accountNameAr : account.accountName}</td>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000; text-align: right;">${formatCurrency(account.totalAmount)}</td>
          </tr>
        `;
      });

      // Total Revenue
      tableHTML += `
        <tr style="background: #C8E6C9;">
          <td style="border: 1px solid #ddd; padding: 8px;"></td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: #000; text-align: ${isRTL ? 'right' : 'left'};">${t.totalRevenue}</td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: #000; text-align: right; font-size: 14px;">${formatCurrency(reportData.grandTotalRevenue)}</td>
        </tr>
        <tr><td colspan="3" style="border: none; padding: 5px;"></td></tr>
        <tr style="background: #EF5350; color: white;">
          <td colspan="3" style="border: 1px solid #ddd; padding: 8px; font-weight: bold;">${t.expenses}</td>
        </tr>
      `;

      // Expense Accounts
      reportData.expenseAccounts.forEach((account) => {
        tableHTML += `
          <tr>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000;">${account.accountCode}</td>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000; text-align: ${isRTL ? 'right' : 'left'};">${isRTL ? account.accountNameAr : account.accountName}</td>
            <td style="border: 1px solid #ddd; padding: 8px; color: #000; text-align: right;">${formatCurrency(account.totalAmount)}</td>
          </tr>
        `;
      });

      // Total Expenses
      tableHTML += `
        <tr style="background: #FFCDD2;">
          <td style="border: 1px solid #ddd; padding: 8px;"></td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: #000; text-align: ${isRTL ? 'right' : 'left'};">${t.totalExpenses}</td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: #000; text-align: right; font-size: 14px;">${formatCurrency(reportData.grandTotalExpenses)}</td>
        </tr>
        <tr><td colspan="3" style="border: none; padding: 5px;"></td></tr>
        <tr style="background: #E3F2FD;">
          <td style="border: 1px solid #ddd; padding: 8px;"></td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: #000; text-align: ${isRTL ? 'right' : 'left'}; font-size: 14px;">${reportData.grandNetProfit >= 0 ? t.netProfit : t.netLoss}</td>
          <td style="border: 1px solid #ddd; padding: 8px; font-weight: bold; color: ${reportData.grandNetProfit >= 0 ? '#1B5E20' : '#B71C1C'}; text-align: right; font-size: 16px;">${formatWithSign(reportData.grandNetProfit)}</td>
        </tr>
      `;

      tableHTML += `
          </tbody>
        </table>
      `;

      tableDiv.innerHTML = tableHTML;
      document.body.appendChild(tableDiv);

      // Table كصورة
      const canvas = await html2canvas(tableDiv, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });

      document.body.removeChild(tableDiv);

      const imgData = canvas.toDataURL('image/png');
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      // التحقق من الحاجة لصفحات متعددة
      let heightLeft = imgHeight;
      let position = yPosition;

      doc.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pageHeight - position - 10);

      while (heightLeft > 0) {
        position = heightLeft - imgHeight + 10;
        doc.addPage();
        doc.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= (pageHeight - 10);
      }

      doc.save(`${t.title}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(t.exportPdf + ' ✔');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    }
  };

  const handlePrint = () => {
    if (!reportData) return;

    const revenueRows = reportData.revenueAccounts.map((a, i) => `
      <tr${i % 2 === 1 ? ' style="background:#f9f9f9;"' : ''}>
        <td>${a.accountCode}</td>
        <td>${isRTL ? a.accountNameAr : a.accountName}</td>
        <td class="num">${formatCurrency(a.totalAmount)}</td>
      </tr>`).join('');

    const expenseRows = reportData.expenseAccounts.map((a, i) => `
      <tr${i % 2 === 1 ? ' style="background:#f9f9f9;"' : ''}>
        <td>${a.accountCode}</td>
        <td>${isRTL ? a.accountNameAr : a.accountName}</td>
        <td class="num">${formatCurrency(a.totalAmount)}</td>
      </tr>`).join('');

    const netColor = reportData.grandNetProfit >= 0 ? '#1B5E20' : '#B71C1C';
    const netLabel = reportData.grandNetProfit >= 0 ? t.netProfit : t.netLoss;

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}">
<head><title>${t.title}</title>
<style>
  body { font-family: Arial, sans-serif; margin: 20px; font-size: 11px; color: #000; }
  .header { text-align: center; margin-bottom: 15px; }
  .header img { max-height: 50px; display: block; margin: 0 auto 8px; }
  .header h1 { margin: 3px 0; font-size: 16px; }
  .header h2 { margin: 3px 0; font-size: 14px; color: #FF6B35; }
  .info-box { background: #E3F2FD; padding: 10px; margin-bottom: 15px; text-align: center; border-radius: 4px; }
  .info-box p { margin: 3px 0; font-size: 11px; }
  .info-box .branch { font-size: 13px; font-weight: bold; color: #1565C0; }
  table { width: 100%; border-collapse: collapse; margin-top: 6px; }
  th, td { border: 1px solid #ddd; padding: 6px 8px; text-align: ${isRTL ? 'right' : 'left'}; }
  th { background: #2196F3; color: #fff; font-size: 10px; font-weight: bold; }
  .num { text-align: right; font-family: monospace; }
  .section-revenue { background: #66BB6A; color: #fff; font-weight: bold; }
  .section-expense { background: #EF5350; color: #fff; font-weight: bold; }
  .total-revenue { background: #C8E6C9; font-weight: bold; }
  .total-expense { background: #FFCDD2; font-weight: bold; }
  .net-row { background: #E3F2FD; font-weight: bold; font-size: 13px; }
  .spacer td { border: none; height: 8px; }
  @media print { body { margin: 10px; } @page { margin: 12mm; size: A4 portrait; } }
</style></head>
<body>
  <div class="header">
    ${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}" />` : ''}
    <h1>${isRTL ? (companyInfo?.companyNameAr || companyInfo?.companyName || '') : (companyInfo?.companyName || '')}</h1>
    ${companyInfo?.address ? `<p style="color:#555;font-size:11px;">${companyInfo.address}</p>` : ''}
    <h2>${t.title}</h2>
  </div>
  <div class="info-box">
    <p class="branch">${t.costCenter}: ${getSelectedBranchName()}</p>
    <p>${t.period}: ${formatDate(fromDate)} — ${formatDate(toDate)}</p>
  </div>
  <table>
    <thead>
      <tr>
        <th style="width:15%">${t.accountCode}</th>
        <th style="width:65%">${t.accountName}</th>
        <th class="num" style="width:20%">${t.amount}</th>
      </tr>
    </thead>
    <tbody>
      <tr class="section-revenue"><td colspan="3">${t.revenue}</td></tr>
      ${revenueRows}
      <tr class="total-revenue">
        <td></td>
        <td>${t.totalRevenue}</td>
        <td class="num">${formatCurrency(reportData.grandTotalRevenue)}</td>
      </tr>
      <tr class="spacer"><td colspan="3"></td></tr>
      <tr class="section-expense"><td colspan="3">${t.expenses}</td></tr>
      ${expenseRows}
      <tr class="total-expense">
        <td></td>
        <td>${t.totalExpenses}</td>
        <td class="num">${formatCurrency(reportData.grandTotalExpenses)}</td>
      </tr>
      <tr class="spacer"><td colspan="3"></td></tr>
      <tr class="net-row">
        <td></td>
        <td style="color:${netColor}">${netLabel}</td>
        <td class="num" style="color:${netColor};font-size:14px">${formatWithSign(reportData.grandNetProfit)}</td>
      </tr>
    </tbody>
  </table>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); setTimeout(() => { w.print(); w.close(); }, 400); }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AccountBalance sx={{ fontSize: 40, color: '#FF6B35' }} />
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#FF6B35' }}>
            {t.title}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadReport} sx={{ color: '#FF6B35' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#4CAF50' }}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPdf} sx={{ color: '#F44336' }}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} sx={{ color: '#2196F3' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2 }}>
          <TextField
            fullWidth
            select
            size="small"
            label={t.costCenter}
            value={selectedCostCenter || ''}
            onChange={(e) => setSelectedCostCenter(e.target.value ? Number(e.target.value) : null)}
          >
            <MenuItem value="">{t.allBranches}</MenuItem>
            {costCenters.map((cc) => (
              <MenuItem key={cc.costCenterId} value={cc.costCenterId}>
                {isRTL ? cc.costCenterNameAr : cc.costCenterName}
              </MenuItem>
            ))}
          </TextField>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.fromDate}
              value={fromDate ? new Date(fromDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setFromDate(newValue);
                if (toDate && newValue && toDate < newValue) setToDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={minDate}
              maxDate={toDate ? new Date(toDate) : maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label={t.toDate}
              value={toDate ? new Date(toDate) : null}
              onChange={(date) => {
                const newValue = date ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}` : '';
                setToDate(newValue);
                if (fromDate && newValue && fromDate > newValue) setFromDate('');
              }}
              format="dd/MM/yyyy"
              views={['year', 'month', 'day']}
              openTo="day"
              minDate={fromDate ? new Date(fromDate) : minDate}
              maxDate={maxDate}
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
        
        {/* معلومات الفرع والفترة */}
        <Box sx={{ mt: 2, textAlign: 'center', py: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="body1" sx={{ color: 'primary.main', fontWeight: 600 }}>
            {t.costCenter}: {getSelectedBranchName()}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
            {t.period}: {formatDate(fromDate)} {t.to} {formatDate(toDate)}
          </Typography>
        </Box>
      </Paper>

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress sx={{ color: '#FF6B35' }} />
        </Box>
      )}

      {/* Report Table */}
      {!loading && reportData && (
        <Paper sx={{ overflow: 'hidden' }}>
          <TableContainer className="report-table-for-pdf">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: '#2196F3' }}>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '15%' }}>{t.accountCode}</TableCell>
                    <TableCell sx={{ color: 'white', fontWeight: 'bold', width: '65%' }}>{t.accountName}</TableCell>
                    <TableCell align="right" sx={{ color: 'white', fontWeight: 'bold', width: '20%' }}>{t.amount}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {/* Revenue Section */}
                  <TableRow sx={{ bgcolor: '#66BB6A' }}>
                    <TableCell colSpan={3} sx={{ color: 'white', fontWeight: 'bold' }}>
                      {t.revenue}
                    </TableCell>
                  </TableRow>

                  {reportData.revenueAccounts.map((account) => (
                    <TableRow key={account.accountId} hover>
                      <TableCell sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {account.accountCode}
                      </TableCell>
                      <TableCell sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {isRTL ? account.accountNameAr : account.accountName}
                      </TableCell>
                      <TableCell align="right" sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {formatCurrency(account.totalAmount)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Total Revenue */}
                  <TableRow sx={{ bgcolor: '#C8E6C9' }}>
                    <TableCell></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#1B5E20', '@media print': { color: '#000 !important' } }}>
                      {t.totalRevenue}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#1B5E20', '@media print': { color: '#000 !important' } }}>
                      {formatCurrency(reportData.grandTotalRevenue)}
                    </TableCell>
                  </TableRow>

                  {/* Empty row */}
                  <TableRow><TableCell colSpan={3} sx={{ height: 10 }}></TableCell></TableRow>

                  {/* Expenses Section */}
                  <TableRow sx={{ bgcolor: '#EF5350' }}>
                    <TableCell colSpan={3} sx={{ color: 'white', fontWeight: 'bold' }}>
                      {t.expenses}
                    </TableCell>
                  </TableRow>

                  {reportData.expenseAccounts.map((account) => (
                    <TableRow key={account.accountId} hover>
                      <TableCell sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {account.accountCode}
                      </TableCell>
                      <TableCell sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {isRTL ? account.accountNameAr : account.accountName}
                      </TableCell>
                      <TableCell align="right" sx={{ '@media print': { color: '#000 !important', fontWeight: 500 } }}>
                        {formatCurrency(account.totalAmount)}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Total Expenses */}
                  <TableRow sx={{ bgcolor: '#FFCDD2' }}>
                    <TableCell></TableCell>
                    <TableCell sx={{ fontWeight: 'bold', color: '#B71C1C', '@media print': { color: '#000 !important' } }}>
                      {t.totalExpenses}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#B71C1C', '@media print': { color: '#000 !important' } }}>
                      {formatCurrency(reportData.grandTotalExpenses)}
                    </TableCell>
                  </TableRow>

                  {/* Empty row */}
                  <TableRow><TableCell colSpan={3} sx={{ height: 10 }}></TableCell></TableRow>

                  {/* Net Profit/Loss */}
                  <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                    <TableCell></TableCell>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      fontSize: '1.1rem', 
                      color: reportData.grandNetProfit >= 0 ? '#1B5E20' : '#B71C1C',
                      '@media print': { color: '#000 !important' } 
                    }}>
                      {reportData.grandNetProfit >= 0 ? t.netProfit : t.netLoss}
                    </TableCell>
                    <TableCell 
                      align="right" 
                      sx={{ 
                        fontWeight: 'bold', 
                        fontSize: '1.2rem',
                        color: reportData.grandNetProfit >= 0 ? '#1B5E20' : '#B71C1C',
                        '@media print': { color: '#000 !important', fontWeight: 'bold' }
                      }}
                    >
                      {formatWithSign(reportData.grandNetProfit)}
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
      )}

      {/* No Data */}
      {!loading && !reportData && (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">{t.noData}</Typography>
        </Paper>
      )}

    </Box>
  );
};

export default BranchProfitLossReport;